import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { MutedSpeakerIcon, UnmutedSpeakerIcon } from '@streamr/tv-ui';
import { useCallback, useState } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { StreamrAppService } from '../../services/streamrapp/StreamrAppService';
import { useTimedFlag } from '../../utils/time';
import { Button } from '../Button';
import { FullscreenButton } from '../FullscreenButton';
import { Hud } from '../Hud';
import { LoadingIndicator } from '../LoadingIndicator';
import { Stage } from '../Stage';
import { VideoDisplay } from '../VideoDisplay';
import { VideoOverlayDisplay } from '../VideoOverlayDisplay';
import { ViewerPreLive } from '../ViewerPreLive';
import { QualityBadge } from '../viewer/QualityBadge';
import { SlowConnectionNotice } from '../viewer/SlowConnectionNotice';
import { DisplayOutput } from './DisplayOutput';
const GlobalStyle = createGlobalStyle `
    body {
        --Stage_PaddingTop: 0rem;
        --Stage_PaddingBottom: 0rem;
        --Stage_PaddingLeft: 0rem;
        --Stage_PaddingRight: 0rem;
    }
`;
/**
 * Chrome-less watch-only view for /embed/:streamId, backed by the streamrapp
 * engine. The external streamrtv-recorder captures this page in headless
 * Chrome, so its automation contract must be preserved: a `[data-playing]`
 * element exists while video is streaming, and the mute button is reachable
 * as `[data-automation-id="unmute"]`.
 */
export function StreamrAppEmbedView() {
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const isVideoStreaming = StreamrAppService.getOutput().useIsVideoStreaming();
    const ready = StreamrAppService.getOutput().useReady();
    const streamrAppService = StreamrAppService.getNotificationInterface();
    const [isHudVisible, showHud] = useTimedFlag(true, 5000);
    const [root, setRoot] = useState(null);
    const handleCanvasMount = useCallback((canvas) => {
        if (canvas) {
            streamrAppService.notifyCanvasMounted(canvas);
        }
    }, [streamrAppService]);
    return (_jsxs(_Fragment, { children: [_jsx(GlobalStyle, {}), isVideoStreaming && (_jsx(_Fragment, { children: _jsx("div", { "data-playing": true }) })), _jsxs(EmbedViewRoot, { ref: setRoot, onMouseDown: () => {
                    showHud();
                }, children: [_jsx(LoadingIndicator, { loading: !ready }), _jsxs(Stage, { children: [_jsx(VideoDisplay, { component: _jsx(DisplayOutput, { onCanvasMount: handleCanvasMount }) }), !isVideoStreaming && (_jsx(ViewerPreLive, { roundedCorners: false, broadcastStreamId: activeBroadcast.streamId, broadcasterUserId: activeBroadcast.broadcasterUserId }))] }), _jsx(SlowConnectionNotice, {}), _jsx(VideoOverlayDisplay, { children: _jsx(StreamrAppEmbedHud, { visible: isHudVisible, fullscreenButton: _jsx(FullscreenButton, { element: root }) }) })] })] }));
}
function StreamrAppEmbedHud({ visible, fullscreenButton, }) {
    const audioMuted = !!StreamrAppService.getOutput().useAudioMuted();
    const audioContextBlocked = StreamrAppService.getOutput().useAudioContextBlocked();
    const streamrAppService = StreamrAppService.getNotificationInterface();
    const showsAsMuted = audioMuted || audioContextBlocked;
    return (_jsxs(EmbedHud, { visible: visible, children: [_jsxs(TopUi, { children: [_jsx(QualityBadge, {}), _jsx("div", { children: fullscreenButton })] }), _jsx("div", {}), _jsx(BottomUi, { children: _jsx(Button
                // Used by browser automation to unmute the video
                , { "data-automation-id": "unmute", type: "button", shape: "pill", padding: "none", kind: showsAsMuted ? undefined : 'secondary', variant: showsAsMuted ? undefined : 'dark', icon: showsAsMuted ? (_jsx(MutedSpeakerIcon, {})) : (_jsx(UnmutedSpeakerIcon, {})), onClick: () => {
                        if (!audioMuted && audioContextBlocked) {
                            // Not user-muted, only blocked by the autoplay
                            // policy — the click gesture unlocks playback.
                            streamrAppService.notifyUserGesture();
                            return;
                        }
                        streamrAppService.notifyToggleAudio();
                    } }) })] }));
}
const Ui = styled.div `
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
`;
const TopUi = styled(Ui) `
    button {
        pointer-events: all;
    }
`;
const BottomUi = styled(Ui) `
    > * {
        pointer-events: all;
    }
`;
const EmbedHud = styled(Hud) `
    display: grid;
    grid-template-rows: auto 1fr auto;
    padding: 1.25rem;
    box-sizing: border-box;
`;
const EmbedViewRoot = styled.div `
    width: 100vw;
    height: 100vh;
    position: relative;
`;
